import { PaymentMethodConfiguration } from '../model';

export interface QualifierCondition {
  type: 'startWith' | 'equal';
  value: string;
}

type BasePaymentMethodConfigurationQualifier<
  C extends keyof PaymentMethodConfiguration,
  P extends keyof PaymentMethodConfiguration[C],
> = {
  conditions: QualifierCondition[];
  result: {
    component: C;
    property: P;
    value: PaymentMethodConfiguration[C][P];
  };
};

type PaymentMethodConfigurationQualifier = {
  [C in keyof PaymentMethodConfiguration]: {
    [P in keyof PaymentMethodConfiguration[C]]: BasePaymentMethodConfigurationQualifier<
      C,
      P
    >;
  }[keyof PaymentMethodConfiguration[C]];
}[keyof PaymentMethodConfiguration];

export const paymentMethodConfigurationQualifiers: PaymentMethodConfigurationQualifier[] =
  [
    {
      conditions: [
        { type: 'startWith', value: 'HeroPaymentsCrypto' },
        { type: 'startWith', value: 'HeroPaymentsFiat' },
        { type: 'startWith', value: 'HeroPaymentsCustody' },
        { type: 'startWith', value: 'Changelly' },
        { type: 'startWith', value: 'Gammag' },
        { type: 'equal', value: 'PagsmileChileMach' },
        { type: 'equal', value: 'CommerGatePIX' },
        { type: 'equal', value: 'PaykassmaUpiS' },
        { type: 'equal', value: 'SmartfastpayAccountPIX' },
        { type: 'equal', value: 'IPaguePIX' },
        { type: 'equal', value: 'BitoloPIX' },
        { type: 'equal', value: 'ProDataQRPH' },
        { type: 'equal', value: 'MacPayPIXQR' },
        { type: 'equal', value: 'VolutiPIX' },
        { type: 'equal', value: 'Directa24VPAYQR' },
        { type: 'equal', value: 'LaFintecaPIXFTD' },
        { type: 'equal', value: 'LaFintecaPIX' },
      ],
      result: {
        component: 'copyableText',
        property: 'type',
        value: 'qrCode',
      },
    },
    {
      conditions: [
        { type: 'equal', value: 'HeroPaymentsCryptoXRP' },
        { type: 'equal', value: 'HeroPaymentsFiatXLM' },
        { type: 'equal', value: 'HeroPaymentsFiatRipple' },
        { type: 'equal', value: 'HeroPaymentsCustodyCryptoXRP' },
        { type: 'equal', value: 'ChangellyBCH' },
        { type: 'equal', value: 'ChangellyBTC' },
        { type: 'equal', value: 'ChangellyDOGE' },
        { type: 'equal', value: 'ChangellyETH' },
        { type: 'equal', value: 'ChangellyLTC' },
        { type: 'equal', value: 'ChangellyTON' },
        { type: 'equal', value: 'ChangellyTRX' },
        { type: 'equal', value: 'ChangellyUSDT' },
        { type: 'equal', value: 'ChangellyXRP' },
        { type: 'equal', value: 'GammagXRP' },
      ],
      result: {
        component: 'copyableText',
        property: 'type',
        value: 'alternative',
      },
    },
    {
      conditions: [
        { type: 'equal', value: 'LocalPaymentRedDePagosHeyCostaRica' },
        { type: 'equal', value: 'LocalPaymentTeleDolarCostaRica' },
        { type: 'equal', value: 'LocalPaymentBancoGTContinentalGuatemala' },
        { type: 'equal', value: 'LocalPaymentCOAC29deOctubreEcuador' },
        { type: 'equal', value: 'LocalPaymentCOAVEcuador' },
        { type: 'equal', value: 'LocalPaymentCoopmegoEcuador' },
        { type: 'equal', value: 'LocalPaymentDIFCruzAzulEcuador' },
        { type: 'equal', value: 'LocalPaymentFacilitoEcuador' },
        { type: 'equal', value: 'LocalPaymentFarmacias911Ecuador' },
        { type: 'equal', value: 'LocalPaymentFarmaciasEconomicasEcuador' },
        { type: 'equal', value: 'LocalPaymentHOUSEEcuador' },
        { type: 'equal', value: 'LocalPaymentJardinAzuayoEcuador' },
        { type: 'equal', value: 'LocalPaymentMedicityEcuador' },
        { type: 'equal', value: 'LocalPaymentMiComisariatoEcuador' },
        { type: 'equal', value: 'LocalPaymentMutualistaPichinchaEcuador' },
        { type: 'equal', value: 'LocalPaymentRianxeriaEcuador' },
        { type: 'equal', value: 'LocalPaymentRNXEcuador' },
        { type: 'equal', value: 'LocalPaymentTiaEcuador' },
        { type: 'equal', value: 'LocalPaymentUnipuntosEcuador' },
        { type: 'equal', value: 'LocalPaymentWesternUnionEcuador' },
        { type: 'equal', value: 'LocalPaymentZonaPagoEcuador' },
      ],
      result: {
        component: 'qrCode',
        property: 'type',
        value: 'rectangle',
      },
    },
    {
      conditions: [
        { type: 'equal', value: 'HeroPaymentsCryptoBTC' },
        { type: 'equal', value: 'HeroPaymentsCryptoBNB' },
        { type: 'equal', value: 'HeroPaymentsCryptoDOGE' },
        { type: 'equal', value: 'HeroPaymentsCryptoLTC' },
        { type: 'equal', value: 'HeroPaymentsCryptoTRX' },
        { type: 'equal', value: 'HeroPaymentsCryptoUSDT' },
        { type: 'equal', value: 'HeroPaymentsCryptoXRP' },
        { type: 'equal', value: 'HeroPaymentsCryptoBCH' },
        { type: 'equal', value: 'HeroPaymentsCryptoETH' },
      ],
      result: {
        component: 'paymentResult',
        property: 'paymentMethodLogo',
        value: 'hidden',
      },
    },
    {
      conditions: [{ type: 'equal', value: 'SpinId' }],
      result: {
        component: 'qrCode',
        property: 'behaviourType',
        value: 'redirect',
      },
    },
    {
      conditions: [{ type: 'equal', value: 'SpinId' }],
      result: {
        component: 'paymentResult',
        property: 'completionBehaviour',
        value: 'showStatus',
      },
    },
  ];
